import { Component } from '@theme/component';

/**
 * `<size-chart-row>` — the size chart disclosure that quick add shows in place
 * of the product page's open chart image.
 *
 * A button and a `hidden` panel, not `<details>`. `<details>` was the obvious
 * choice and cannot do the job: the same block has to be a collapsible
 * disclosure inside the quick-add dialog and absent on the product page behind
 * it, and `open` is a DOM attribute no stylesheet can set. A button whose state
 * lives in `aria-expanded` and `hidden` is togglable from one context and
 * inert in the other.
 *
 * The panel is hidden with the `hidden` property rather than an inline
 * `display`, so the attribute the accessibility tree reads and the pixels on
 * screen can never disagree — and `.size-chart-row__body[hidden]` carries an
 * explicit `display: none` beside the rule that gives the body a display at
 * all, because `hidden` alone loses to any author rule. See
 * `scripts/test_fit_profile_hidden_guards.py`, which is the write-up of the
 * four times this feature got that wrong.
 *
 * Loaded from `theme/snippets/scripts.liquid` like every other component
 * module, NOT from the block. The block renders only on a product template,
 * and the row's whole reason to exist is the quick-add dialog on a collection
 * page — where the markup arrives by morph and a `<script>` tag inside it
 * would never run.
 *
 * @extends {Component<{summary: HTMLButtonElement, body: HTMLElement}>}
 */
class SizeChartRow extends Component {
  /**
   * Deliberately no `requiredRefs`. A missing ref throws out of
   * `connectedCallback`, and this element is connected by `morph` in the
   * middle of rebuilding the dialog's contents; a throw there would take the
   * rest of the modal's render with it. The row failing to open is a worse
   * outcome than the row opening, but it is a far better one than the modal
   * not rendering, so the guards below fail quiet instead.
   *
   * @param {Event} [event]
   */
  toggle = (event) => {
    event?.preventDefault();

    const { summary, body } = this.refs;
    if (!(summary instanceof HTMLElement) || !(body instanceof HTMLElement)) return;

    const opening = body.hidden;
    body.hidden = !opening;
    summary.setAttribute('aria-expanded', String(opening));
  };
}

if (!customElements.get('size-chart-row')) {
  customElements.define('size-chart-row', SizeChartRow);
}
